import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    lang,
    setLang,
    t
  } = useLanguage();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);
  const navLinks = [{
    label: t.nav.inicio,
    href: '#inicio'
  }, {
    label: t.nav.servicios,
    href: '#servicios'
  }, {
    label: t.nav.portfolio,
    href: '/portafolio',
    route: true
  }, {
    label: t.nav.sobre,
    href: '#sobre'
  }, {
    label: t.nav.faq,
    href: '#faq'
  }, {
    label: t.nav.contact,
    href: '#contacto'
  }];
  const isHome = typeof window !== 'undefined' && window.location.pathname.replace(/\/$/, '') === '';
  const resolveHref = (link: {
    href: string;
    route?: boolean;
  }) => {
    if (link.route) return link.href;
    if (link.href.startsWith('#') && !isHome) return `/${link.href}`;
    return link.href;
  };
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, link: {
    href: string;
    route?: boolean;
  }) => {
    setIsMenuOpen(false);
    if (link.route) {
      e.preventDefault();
      window.history.pushState({}, '', link.href);
      window.dispatchEvent(new Event('mp:navigate'));
      window.scrollTo(0, 0);
    }
  };
  const handleLinkClick = () => {
    setIsMenuOpen(false);
  };
  return <>
      <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5
    }} className={`fixed top-0 left-0 w-full z-[100] transition-all duration-300 bg-[rgba(250,248,245,0.95)] backdrop-blur-[8px] border-b border-inkova-primary/10 min-h-[60px] lg:min-h-[68px] flex items-center ${isScrolled ? 'shadow-[0_2px_12px_rgba(46,31,71,0.06)]' : ''}`} data-id="element-258">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-10 flex justify-between items-center" data-id="element-259">
          <a href="/" onClick={e => {
          setIsMenuOpen(false);
          if (window.location.pathname.endsWith('/portafolio')) {
            e.preventDefault();
            window.history.pushState({}, '', '/');
            window.dispatchEvent(new Event('mp:navigate'));
            window.scrollTo(0, 0);
          }
        }} className="flex flex-col justify-center min-w-[130px] lg:min-w-[160px]" data-id="element-260">
            <img src="https://cdn.magicpatterns.com/uploads/bchCoJR31y9RiGq2Q8WpMF/4.png" alt="Inkova" className="h-[40px]! lg:h-[44px]! w-auto! block object-contain object-left" style={{
            height: '40px',
            width: 'auto'
          }} data-id="element-261" />
            <span className="text-[8px] lg:text-[9px] text-[#6B7280] mt-0.5 block whitespace-nowrap font-medium" data-id="element-262">
              Business Consulting & Creation
            </span>
          </a>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-8" data-id="element-263">
            <div className="flex gap-8" data-id="element-264">
              {navLinks.map(link => <a key={link.href} href={resolveHref(link)} onClick={e => handleNavClick(e, link)} className="text-sm font-medium text-inkova-dark hover:text-inkova-primary transition-colors duration-200" data-id="element-265">
                  {link.label}
                </a>)}
            </div>

            <div className="flex items-center ml-4 bg-inkova-primary/[0.04] rounded-full p-0.5 border border-inkova-primary/10" data-id="element-266">
              <button onClick={() => setLang('es')} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${lang === 'es' ? 'bg-white text-inkova-primary shadow-sm' : 'text-inkova-dark/45 hover:text-inkova-dark'}`} aria-pressed={lang === 'es'} aria-label="Español" data-id="element-267">
                ES
              </button>
              <button onClick={() => setLang('en')} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${lang === 'en' ? 'bg-white text-inkova-primary shadow-sm' : 'text-inkova-dark/45 hover:text-inkova-dark'}`} aria-pressed={lang === 'en'} aria-label="English" data-id="element-268">
                EN
              </button>
            </div>

            <a href="https://wa.me/13054781618" target="_blank" rel="noopener noreferrer" className="px-6 py-2.5 rounded-[50px] font-semibold text-sm bg-[#5A4579] text-white hover:bg-[#3D2F5A] transition-all duration-300" data-id="element-269">
              {t.nav.cta}
            </a>
          </div>

          {/* Mobile: CTA + hamburger */}
          <div className="flex lg:hidden items-center gap-3" data-id="element-270">
            <div className="flex items-center bg-inkova-primary/5 rounded-full p-1 mr-1 border border-inkova-primary/10" data-id="element-271">
              <button onClick={() => setLang('es')} className={`px-2 py-1 rounded-full text-[10px] font-bold transition-all ${lang === 'es' ? 'bg-white text-inkova-primary shadow-sm' : 'text-inkova-dark/60 hover:text-inkova-dark'}`} aria-pressed={lang === 'es'} aria-label="Español" data-id="element-272">
                ES
              </button>
              <button onClick={() => setLang('en')} className={`px-2 py-1 rounded-full text-[10px] font-bold transition-all ${lang === 'en' ? 'bg-white text-inkova-primary shadow-sm' : 'text-inkova-dark/60 hover:text-inkova-dark'}`} aria-pressed={lang === 'en'} aria-label="English" data-id="element-273">
                EN
              </button>
            </div>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-inkova-dark p-2 focus:outline-none" aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'} data-id="element-274">
              {isMenuOpen ? <X className="w-6 h-6" data-id="element-275" /> : <Menu className="w-6 h-6" data-id="element-276" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile menu overlay */}
      <AnimatePresence data-id="element-277">
        {isMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-40 bg-black/20 lg:hidden backdrop-blur-sm" onClick={() => setIsMenuOpen(false)} data-id="element-278" />}
      </AnimatePresence>

      {/* Mobile menu panel */}
      <AnimatePresence data-id="element-279">
        {isMenuOpen && <motion.div initial={{
        y: -20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: -20,
        opacity: 0
      }} transition={{
        type: 'tween',
        duration: 0.2
      }} className="fixed top-[72px] left-0 right-0 bg-white z-50 shadow-xl lg:hidden flex flex-col rounded-b-2xl border-t border-inkova-primary/10" data-id="element-280">
            <nav className="flex flex-col p-6 gap-5" data-id="element-281">
              {navLinks.map((link, i) => <motion.a key={link.href} href={resolveHref(link)} onClick={e => handleNavClick(e, link)} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.05
          }} className="text-base font-medium text-inkova-dark hover:text-inkova-primary transition-colors" data-id="element-282">
                  {link.label}
                </motion.a>)}

              <motion.a href="https://wa.me/13054781618" target="_blank" rel="noopener noreferrer" onClick={handleLinkClick} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: navLinks.length * 0.05
          }} className="mt-2 w-full text-center px-6 py-3 rounded-full bg-inkova-primary text-white font-semibold hover:bg-inkova-dark-purple transition-colors duration-300" data-id="element-283">
                {t.nav.cta}
              </motion.a>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </>;
}